import React, { useCallback, useEffect } from 'react';
import {
    ActionIcon,
    Button,
    Flex,
    Highlight,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { IconArrowRight, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { theme } from '../../../global/style/theme.tsx';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { useElementSize } from '@mantine/hooks';
import { containsStringIgnoreCase } from '../../HcpDashboard/Table/PatientTable.tsx';
import { modals } from '@mantine/modals';
import { useNavigate } from 'react-router-dom';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';
import { RouteStore } from '../../../global/Store/RouteStore.ts';
import { useGetPatientCollaborators } from '../../../global/entities/Collaborator/queries/useGetPatientCollaborators.tsx';
import { useDeleteCollaboratorMutations } from '../../../global/entities/Collaborator/mutations/useDeleteCollaboratorMutations.tsx';
import { ICollaborator } from '../../../global/entities/Collaborator/Collaborator.ts';

export type ICollaboratorTableProps = {
    patientStore?: PatientStore;
    routeStore?: RouteStore;
};

const CollaboratorTable: React.FC<ICollaboratorTableProps> = inject(
    'patientStore',
    'routeStore',
)(
    observer(({ patientStore, routeStore }) => {
        const navigate = useNavigate();
        const { currentUser } = useSessionProvider();
        const { t } = useTranslation();

        const { data: collaborators } = useGetPatientCollaborators();
        const deleteCollaborator = useDeleteCollaboratorMutations();

        const { setSortedData, sortedData, sortBy, reverseSortDirection, setSorting, search, setSearch } = useTable();
        const { ref, width } = useElementSize();

        useEffect(() => {
            setSortedData([]);
            if (collaborators?.length) 
                setSortedData(collaborators);
            
        }, [collaborators, setSortedData]);

        useEffect(() => {
            routeStore?.setFromLocation();
        }, []);

        const openModalDelete = (id: string) => {
            const patientId = patientStore?.patientId || currentUser?.patientID;
            if (!patientId) 
                return;
            
            return modals.openConfirmModal({
                title: t('modal.deleteItemModal.title'),
                children: (
                    <Stack gap={'sm'}>
                        <Text>{t('modal.deleteItemModal.question')}</Text>
                        <Text fw={'bold'}> {t('modal.deleteItemModal.details')}</Text>
                    </Stack>
                ),
                labels: { confirm: t('button.delete'), cancel: t('button.cancel') },
                confirmProps: { color: 'red' },
                cancelProps: { variant: 'outline', color: 'gray' },
                onCancel: () => console.log('Suppression annulée'),
                onConfirm: () => onClickDelete(id, patientId),
                size: 'lg',
                radius: 'xs',
            });
        };

        const onClickDelete = useCallback(
            (taskId: string, patientId: string) => {
                if (!taskId || !patientId) 
                    return;
                
                deleteCollaborator.mutateAsync({
                    id: taskId,
                });
            },
            [deleteCollaborator],
        );
        const handleCollaboratorEvents = (id?: number) => {
            navigate(`../collaborator/${id}`);
        };

        return (
            <Paper ref={ref} h={'min-content'} w={'100%'}>
                <Stack h={'100%'}>
                    <Flex align={'center'} gap={'sm'}>
                        <TextInput
                            hidden={width < 450}
                            placeholder={t('global.search')}
                            rightSection={width < 450 ? null : <IconSearch />}
                            value={search}
                            color={'var(--mantine-color-default-color)'}
                            style={{ flex: 1 }}
                            radius={'md'}
                            onChange={(el) => setSearch(el.currentTarget.value)}
                        />
                        <Button
                            variant="filled"
                            leftSection={<IconPlus size={14} />}
                            onClick={() => handleCollaboratorEvents(0)}
                            w={width <= 450 ? '100%' : undefined}
                        >
                            {t('formTask.addCollaborator')}
                        </Button>
                    </Flex>
                    <ScrollArea w="100%">
                        <Table style={{ tableLayout: 'auto' }}>
                            <Table.Thead>
                                <Table.Tr>
                                    <Th
                                        sorted={sortBy === 'firstName'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('firstName', sortedData)}
                                    >
                                        {t('formGlobal.firstName')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'lastName'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('lastName', sortedData)}
                                    >
                                        {t('formGlobal.lastName')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'email'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('email', sortedData)}
                                    >
                                        {t('formGlobal.email')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'role'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('role', sortedData)}
                                        visibleFrom={theme.breakpoints?.xs}
                                    >
                                        {t('formGlobal.role')}
                                    </Th>
                                    <Table.Th maw={50} />
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {sortedData?.map((row: ICollaborator, index) => {
                                    const isSearch =
                                        containsStringIgnoreCase(
                                            [`${row.firstName}`, `${row.lastName}`, `${row.email}`, `${row.role}`],
                                            search,
                                        ) || !search;
                                    return (
                                        <Table.Tr
                                            key={index}
                                            style={{
                                                background:
                                                    index % 2 === 1
                                                        ? 'var(--Background-Gray-light, #E9ECEF)'
                                                        : undefined,
                                            }}
                                            hidden={!isSearch}
                                        >
                                            <Table.Td>
                                                <Highlight
                                                    highlight={search}
                                                    highlightStyles={{
                                                        color: 'var(--mantine-color-gray-9)',
                                                        fontWeight: 700,
                                                        WebkitBackgroundClip: 'text',
                                                    }}
                                                    styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                    truncate
                                                >
                                                    {row.firstName}
                                                </Highlight>
                                            </Table.Td>
                                            <Table.Td>
                                                <Highlight
                                                    highlight={search}
                                                    highlightStyles={{
                                                        color: 'var(--mantine-color-gray-9)',
                                                        fontWeight: 700,
                                                        WebkitBackgroundClip: 'text',
                                                    }}
                                                    styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                    truncate
                                                >
                                                    {row.lastName}
                                                </Highlight>
                                            </Table.Td>

                                            <Table.Td>
                                                <Highlight
                                                    highlight={search}
                                                    highlightStyles={{
                                                        color: 'var(--mantine-color-gray-9)',
                                                        fontWeight: 700,
                                                        WebkitBackgroundClip: 'text',
                                                    }}
                                                    styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                    truncate
                                                >
                                                    {row.email}
                                                </Highlight>
                                            </Table.Td>
                                            <Table.Td visibleFrom={theme.breakpoints?.md}>
                                                <Highlight
                                                    highlight={search}
                                                    highlightStyles={{
                                                        color: 'var(--mantine-color-gray-9)',
                                                        fontWeight: 700,
                                                        WebkitBackgroundClip: 'text',
                                                    }}
                                                    styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                    truncate
                                                >
                                                    {row.role}
                                                </Highlight>
                                            </Table.Td>
                                            <Table.Td>
                                                <Flex align={'center'}>
                                                    <Tooltip 
                                                        label={row.hasDependencies 
                                                            ? t('toolTip.cannotDeleteCollaboratorWithTasks') 
                                                            : t('toolTip.delete')
                                                        }
                                                    >
                                                        <ActionIcon
                                                            disabled={row.hasDependencies}
                                                            variant="subtle"
                                                            color={row.hasDependencies ? 'gray' : 'red'}
                                                            onClick={() => openModalDelete(row.id!.toString())}
                                                        >
                                                            <IconTrash size={18} />
                                                        </ActionIcon>
                                                    </Tooltip>

                                                    <ActionIcon
                                                        variant={'transparent'}
                                                        onClick={() => handleCollaboratorEvents(row.id)}
                                                    >
                                                        <IconArrowRight color={theme.colors?.blue?.toString()} />
                                                    </ActionIcon>
                                                </Flex>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                                                    </Table.Tbody>
                    </Table>
                </ScrollArea>
            </Stack>
            </Paper>
        );
    }),
);

const WrappedCollaboratorTable: React.FC = () => {
    return (
        <TableContextProvider>
            <CollaboratorTable />
        </TableContextProvider>
    );
};

export default WrappedCollaboratorTable;
